/**
 * ShopLogger - A lightweight logging utility for Shopify themes
 * 
 * This utility provides a consistent logging interface across the theme's JavaScript files,
 * with the ability to enable/disable logs and set different logging levels.
 * 
 * USAGE:
 * 
 * 1. Basic usage in JavaScript files:
 *    - First, create a namespaced logger at the top of your file:
 *      ```
 *      const Logger = window.ShopLogger ? 
 *        window.ShopLogger.createLogger('YourComponentName') : 
 *        { error: console.error, warn: console.warn, info: () => {}, debug: () => {} };
 *      ```
 *    - Then use it throughout your file:
 *      ```
 *      Logger.debug('Some debug message', optionalData);
 *      Logger.info('Some info message', optionalData);
 *      Logger.warn('Some warning message', optionalData);
 *      Logger.error('Some error message', optionalData);
 *      ```
 * 
 * 2. Enabling logs in the browser console:
 *    - Enable all logs: `window.enableShopLogs('debug')`
 *    - Enable only warnings and errors: `window.enableShopLogs('warn')`
 *    - Enable only errors: `window.enableShopLogs('error')`
 *    - Enable logs for a specific component:
 *      ```
 *      window.ShopLogger.disable();
 *      window.ShopLogger.createLogger('CartCalculator').enable('debug');
 *      ```
 *    - Disable all logs: `window.disableShopLogs()`
 * 
 * 3. Log levels:
 *    - 'error': Only show errors (highest severity)
 *    - 'warn': Show warnings and errors
 *    - 'info': Show info messages, warnings, and errors
 *    - 'debug': Show all logs (lowest severity)
 * 
 * By default, logs are disabled in production to avoid cluttering the console
 * for regular users. Developers can enable them as needed for debugging.
 */
const ShopLogger = {
  // Default to disabled in production
  enabled: false,
  
  // Log levels
  ERROR: 'error',
  WARN: 'warn',
  INFO: 'info',
  DEBUG: 'debug',
  
  // Current log level - only logs at this level or higher will be shown
  level: 'error',
  
  // Enable logging with optional level
  enable(level) {
    this.enabled = true;
    if (level) this.level = level;
    this.info('Logging enabled at level: ' + this.level);
    return this; // For chaining
  },
  
  // Disable logging
  disable() {
    this.enabled = false;
    return this; // For chaining
  },
  
  // Create a namespaced logger
  createLogger(namespace) {
    const logger = Object.create(this);
    logger.namespace = namespace;
    return logger;
  },
  
  // Format message with namespace
  formatMessage(message) {
    return `[${this.namespace || 'Shop'}] ${message}`;
  },
  
  // Log methods
  error(message, data) {
    if (this.enabled) {
      console.error(this.formatMessage(message), data || '');
    }
  },
  
  warn(message, data) {
    if (this.enabled && ['warn', 'info', 'debug'].includes(this.level)) {
      console.warn(this.formatMessage(message), data || '');
    }
  },
  
  info(message, data) {
    if (this.enabled && ['info', 'debug'].includes(this.level)) {
      console.info(this.formatMessage(message), data || '');
    }
  },
  
  debug(message, data) {
    if (this.enabled && this.level === 'debug') {
      console.log(this.formatMessage(message), data || '');
    }
  }
};

// Expose logger to window for developer access
window.ShopLogger = ShopLogger;
window.enableShopLogs = (level) => ShopLogger.enable(level);
window.disableShopLogs = () => ShopLogger.disable();
